import { useEffect, useState } from "react";

import styles from "./AgentTasksIndicator.module.css";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/Popover";
import { type ApiAgentTask, useAgentTasks } from "../../data/api/tasks";
import { cx } from "../../lib/cx";

// An age, not a timestamp: everything on this panel is running.
const elapsed = (startedAt: string, now: number): string => {
  const started = Date.parse(startedAt);
  if (Number.isNaN(started)) {
    return "";
  }
  const seconds = Math.max(0, Math.round((now - started) / 1000));
  if (seconds < 60) {
    return `${seconds.toString()}s`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes.toString()}m`;
  }
  return `${Math.floor(minutes / 60).toString()}h ${(minutes % 60).toString()}m`;
};

// What the task *is*: the subagent's type or the workflow's name, falling back
// to the SDK's kind for a shell, which has neither.
const label = (task: ApiAgentTask): string => task.agent ?? task.kind;

const meta = (task: ApiAgentTask, now: number): string =>
  [
    elapsed(task.startedAt, now),
    task.toolUses === undefined
      ? undefined
      : `${task.toolUses.toString()} tool${task.toolUses === 1 ? "" : "s"}`,
    task.lastTool,
    task.backgrounded ? "background" : undefined,
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * What the agent has running right now (`agents-and-tasks.md` §What the agent
 * is running) — in the header rather than the chat, because background work
 * outlives the turn that started it and a reader watching the Diff tab is
 * exactly who needs to know it is still going.
 *
 * Absent when nothing is running, which is most of the time.
 */
export const AgentTasksIndicator = ({
  taskId,
}: {
  readonly taskId: string;
}) => {
  const tasks = useAgentTasks(taskId);
  return tasks.length === 0 ? null : <Indicator tasks={tasks} />;
};

/**
 * Split out so that "the list is open" and the clock driving it live only as
 * long as there is something running.
 *
 * Held above the empty check instead, they outlived it: the indicator vanishes
 * whenever the agent finishes its last task, and a stale `open` then reopened
 * the panel by itself on the next subagent.
 */
const Indicator = ({
  tasks,
}: {
  readonly tasks: ReadonlyArray<ApiAgentTask>;
}) => {
  const [open, setOpen] = useState(false);
  // Only while the list is on screen: the ages are the one thing here that
  // moves without an event behind it, and a ticking clock nobody is reading is
  // a re-render a second for the life of the task.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [open]);
  // Opening re-reads the clock here rather than in the effect, so the first
  // frame is not up to a second stale from the last time the list was open.
  const onOpenChange = (next: boolean): void => {
    if (next) {
      setNow(Date.now());
    }
    setOpen(next);
  };

  return (
    <Popover onOpenChange={onOpenChange} open={open}>
      <PopoverTrigger asChild>
        {/* The chat's own "still going" cue rather than a `Pill`, which is for
            states that hold still. The pulse is the whole point: the pills
            beside it describe the task, this describes something happening. */}
        <button
          className={cx("thinking", styles.trigger)}
          title="What the agent is running"
          type="button"
        >
          <span className="dot" />
          {tasks.length} running
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6}>
        <div className={styles.list}>
          {tasks.map((task) => (
            <div className={styles.task} key={task.id}>
              <div className={styles.head}>
                <span className={styles.name}>{label(task)}</span>
                <span className={styles.meta}>{meta(task, now)}</span>
              </div>
              <div className={styles.description}>{task.description}</div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
